import { css as _css } from "@emotion/core";
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { getMessageConfig, messageTypes } from './config';
import * as track from '../../util/analytics';
import ActionButtonOrLink, { actionPropType } from './action-button-or-link';
import * as styles from './message.style';
import { jsx as ___EmotionJSX } from "@emotion/core";

var Message = function Message(_ref) {
  var headline = _ref.headline,
      detail = _ref.detail,
      iconProp = _ref.icon,
      onCloseClick = _ref.onCloseClick,
      type = _ref.type,
      isToast = _ref.isToast,
      isAlert = _ref.isAlert,
      action = _ref.action,
      children = _ref.children,
      getMixpanelBaseProps = _ref.getMixpanelBaseProps,
      highlight = _ref.highlight;

  var _useInView = useInView({
    triggerOnce: true
  }),
      inView = _useInView.inView,
      ref = _useInView.ref;

  var hasHeadline = !!(headline !== null && headline !== void 0 && headline.length);
  var isInteractive = !!(action !== null && action !== void 0 && action.text || onCloseClick);
  var isWholeMessageClickable = isInteractive && hasHeadline; // we don't want whole message clickable if using children prop

  var icon = iconProp !== null && iconProp !== void 0 ? iconProp : getMessageConfig(type).icon;

  var handleActionClick = function handleActionClick(handler) {
    var _action$link;

    track.actionedMessage({
      messageHeadline: headline,
      actionTaken: action.text,
      actionUrl: (_action$link = action.link) === null || _action$link === void 0 ? void 0 : _action$link.href
    }, getMixpanelBaseProps);

    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    handler.apply(void 0, args);
  };

  var handleCloseClick = function handleCloseClick() {
    track.actionedMessage({
      messageHeadline: headline,
      actionTaken: 'Hide Message'
    }, getMixpanelBaseProps);
    onCloseClick.apply(void 0, arguments);
  };

  useEffect(function () {
    if (inView) {
      track.viewedMessage({
        messageHeadline: headline
      }, getMixpanelBaseProps);
    }
  }, [inView, getMixpanelBaseProps, headline]);
  return ___EmotionJSX("div", {
    css: /*#__PURE__*/_css([styles.message(isToast), styles.messageIs(type, isWholeMessageClickable), highlight && styles.noBorders]),
    ref: ref,
    "data-testid": "messages__message-wrapper"
  }, ___EmotionJSX("div", {
    css: styles.messageContent
  }, icon !== false && ___EmotionJSX("div", {
    css: styles.messageIcon,
    key: "icon",
    "data-testid": "message-icon"
  }, icon), ___EmotionJSX("div", {
    css: styles.messageHeadline(isToast, !!detail),
    role: isToast || isAlert ? 'alert' : 'none',
    "data-testid": "messages__message-text"
  }, headline || children), ___EmotionJSX(ActionButtonOrLink, {
    action: action,
    onCloseClick: onCloseClick ? handleCloseClick : undefined,
    onActionClick: handleActionClick,
    isWholeMessageClickable: isWholeMessageClickable
  })), !isToast && detail && ___EmotionJSX("div", {
    css: styles.messageDetail
  }, detail));
};

var deprecationMsg = "DEPRECATED: <Message /> component \"children\" prop";
var newContentMsg = "\"headline\" and \"detail\" are the correct props to be used for content in the <Message /> component";
var warningsCache = {};

var validateChildren = function validateChildren(props, propName, componentName) {
  var headline = props.headline,
      children = props.children;

  if (children) {
    if (headline !== null && headline !== void 0 && headline.length && !warningsCache.childrenAndHeadline) {
      warningsCache.childrenAndHeadline = true;
      console.error("".concat(deprecationMsg, " - React children will not be rendered when the \"headline\" prop is also passed. ").concat(newContentMsg, "."));
    }

    if (!warningsCache.childrenNoHeadline) {
      warningsCache.childrenNoHeadline = true;
      console.error("".concat(deprecationMsg, " - ").concat(newContentMsg, ". Please update your implementation to reflect this."));
    }
  }

  if (!children && !headline && !warningsCache.noChildrenNoHeadline) {
    warningsCache.noChildrenNoHeadline = true;
    console.error("You must pass \"headline\" prop to the <Message /> component.");
  }

  return PropTypes.checkPropTypes({
    children: PropTypes.node
  }, {
    children: children
  }, propName, componentName);
};

Message.propTypes = {
  headline: PropTypes.string,
  detail: PropTypes.string,
  children: validateChildren,
  type: PropTypes.oneOf(messageTypes).isRequired,
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.instanceOf(false)]),
  onCloseClick: PropTypes.func,
  isToast: PropTypes.bool,
  isAlert: PropTypes.bool,
  action: actionPropType,
  getMixpanelBaseProps: PropTypes.func,
  highlight: PropTypes.bool
};
Message.defaultProps = {
  headline: undefined,
  detail: undefined,
  icon: undefined,
  onCloseClick: undefined,
  isToast: false,
  isAlert: false,
  action: undefined,
  children: null,
  getMixpanelBaseProps: undefined,
  highlight: false
};
export default Message;